import { useCallback, useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react'

export type TextPage = { start: number; end: number; text: string }

type Grapheme = { start: number; end: number }

function graphemes(text: string): Grapheme[] {
  const Segmenter = (Intl as unknown as { Segmenter?: new (locale?: string, options?: { granularity: 'grapheme' }) => { segment: (value: string) => Iterable<{ index: number; segment: string }> } }).Segmenter
  if (Segmenter) {
    const segmenter = new Segmenter(undefined, { granularity: 'grapheme' })
    return Array.from(segmenter.segment(text), part => ({ start: part.index, end: part.index + part.segment.length }))
  }
  const result: Grapheme[] = []
  let offset = 0
  for (const character of Array.from(text)) {
    result.push({ start: offset, end: offset + character.length })
    offset += character.length
  }
  return result
}

function samePages(a: TextPage[], b: TextPage[]): boolean {
  return a.length === b.length && a.every((page, index) => page.start === b[index].start && page.end === b[index].end && page.text === b[index].text)
}

function measureTextPages(text: string, height: number, measureElement: HTMLElement): TextPage[] {
  if (!text) return [{ start: 0, end: 0, text: '' }]
  const units = graphemes(text)
  const fits = (startUnit: number, endUnit: number): boolean => {
    measureElement.textContent = text.slice(units[startUnit]?.start ?? text.length, units[endUnit - 1]?.end ?? 0)
    return measureElement.getBoundingClientRect().height <= height + 0.5
  }
  const pages: TextPage[] = []
  let startUnit = 0
  while (startUnit < units.length) {
    let low = startUnit + 1
    let high = units.length
    let best = startUnit
    while (low <= high) {
      const middle = Math.floor((low + high) / 2)
      if (fits(startUnit, middle)) {
        best = middle
        low = middle + 1
      } else {
        high = middle - 1
      }
    }
    // A single grapheme can be taller than the available area (for example a
    // very large user-selected font). It still has to remain reachable and
    // must not be dropped just because it cannot fit the measured viewport.
    const endUnit = best > startUnit ? best : startUnit + 1
    const start = units[startUnit].start
    const end = units[endUnit - 1].end
    pages.push({ start, end, text: text.slice(start, end) })
    startUnit = endUnit
  }
  measureElement.textContent = ''
  return pages
}

type UseMeasuredPagesOptions = {
  text: string
  viewportRef: RefObject<HTMLElement | null>
  measureRef: RefObject<HTMLElement | null>
  resetKey: string
}

/**
 * Measures the same DOM font used for the visible page and cuts only at
 * grapheme boundaries. The page index is deliberately not part of GameState.
 */
export function useMeasuredPages({ text, viewportRef, measureRef, resetKey }: UseMeasuredPagesOptions) {
  const [pages, setPages] = useState<TextPage[]>([{ start: 0, end: text.length, text }])
  const [pageIndex, setPageIndex] = useState(0)
  const retryFrameRef = useRef<number | null>(null)
  const retryCountRef = useRef(0)

  const measure = useCallback(() => {
    const viewport = viewportRef.current
    const measureElement = measureRef.current
    if (!viewport || !measureElement) return
    const width = viewport.clientWidth
    const height = viewport.clientHeight
    if (width <= 0 || height <= 0) {
      // A viewport transition can briefly produce a zero-sized grid track.
      // ResizeObserver is not guaranteed to emit a second notification after
      // the track settles, so keep a short, cancellable retry window.
      if (retryFrameRef.current === null && retryCountRef.current < 12) {
        retryCountRef.current += 1
        retryFrameRef.current = window.requestAnimationFrame(() => {
          retryFrameRef.current = null
          measure()
        })
      }
      return
    }
    retryCountRef.current = 0
    measureElement.style.width = `${width}px`
    const nextPages = measureTextPages(text, height, measureElement)
    setPages(previous => samePages(previous, nextPages) ? previous : nextPages)
    setPageIndex(previous => Math.min(previous, Math.max(nextPages.length - 1, 0)))
  }, [measureRef, text, viewportRef])

  useLayoutEffect(() => {
    // Do not briefly render the unmeasured full paragraph: on a short viewport
    // that would create a real overflow frame before the first measurement.
    setPages([{ start: 0, end: 0, text: '' }])
    setPageIndex(0)
    retryCountRef.current = 0
    if (retryFrameRef.current !== null) window.cancelAnimationFrame(retryFrameRef.current)
    retryFrameRef.current = null
    const frame = window.requestAnimationFrame(measure)
    return () => {
      window.cancelAnimationFrame(frame)
      if (retryFrameRef.current !== null) window.cancelAnimationFrame(retryFrameRef.current)
      retryFrameRef.current = null
    }
  }, [measure, resetKey, text])

  useEffect(() => {
    const viewport = viewportRef.current
    if (!viewport) return
    let frame = 0
    let active = true
    const schedule = () => {
      if (!active) return
      window.cancelAnimationFrame(frame)
      frame = window.requestAnimationFrame(measure)
    }
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(schedule)
    observer?.observe(viewport)
    window.addEventListener('resize', schedule)
    const fonts = document.fonts
    const fontsReady = fonts?.ready.then(schedule)
    fonts?.addEventListener('loadingdone', schedule)
    return () => {
      window.cancelAnimationFrame(frame)
      if (retryFrameRef.current !== null) window.cancelAnimationFrame(retryFrameRef.current)
      retryFrameRef.current = null
      active = false
      observer?.disconnect()
      window.removeEventListener('resize', schedule)
      fonts?.removeEventListener('loadingdone', schedule)
      void fontsReady?.catch(() => undefined)
    }
  }, [measure, viewportRef])

  return {
    pages,
    pageIndex,
    setPageIndex,
    page: pages[pageIndex] || pages[0],
    isFirst: pageIndex === 0,
    isLast: pageIndex === pages.length - 1,
    measure
  }
}

export type ChoicePage<T> = T[]

type UseChoicePagesOptions<T> = {
  items: T[]
  viewportRef: RefObject<HTMLElement | null>
  measureRef: RefObject<HTMLElement | null>
  resetKey: string
  itemSelector: string
}

function sameChoicePages<T>(a: ChoicePage<T>[], b: ChoicePage<T>[]): boolean {
  return a.length === b.length && a.every((page, pageIndex) => page.length === b[pageIndex].length && page.every((item, itemIndex) => item === b[pageIndex][itemIndex]))
}

export function useMeasuredChoicePages<T>({ items, viewportRef, measureRef, resetKey, itemSelector }: UseChoicePagesOptions<T>) {
  const [pages, setPages] = useState<ChoicePage<T>[]>([items])
  const [pageIndex, setPageIndex] = useState(0)
  const [oversized, setOversized] = useState<Set<number>>(new Set())
  const itemsRef = useRef(items)
  const retryFrameRef = useRef<number | null>(null)
  const retryCountRef = useRef(0)
  itemsRef.current = items

  const measure = useCallback(() => {
    const viewport = viewportRef.current
    const measureElement = measureRef.current
    if (!viewport || !measureElement || viewport.clientHeight <= 0) {
      if (retryFrameRef.current === null && retryCountRef.current < 12) {
        retryCountRef.current += 1
        retryFrameRef.current = window.requestAnimationFrame(() => {
          retryFrameRef.current = null
          measure()
        })
      }
      return
    }
    retryCountRef.current = 0
    const height = viewport.clientHeight
    const currentItems = itemsRef.current
    measureElement.style.width = `${viewport.clientWidth}px`
    const nodes = Array.from(measureElement.querySelectorAll<HTMLElement>(itemSelector))
    if (nodes.length !== currentItems.length) return
    const heights = nodes.map(node => node.getBoundingClientRect().height)
    const nextOversized = new Set(heights.flatMap((itemHeight, index) => itemHeight > height + 0.5 ? [index] : []))
    const nextPages: ChoicePage<T>[] = []
    let page: T[] = []
    let pageHeight = 0
    const gap = Number.parseFloat(getComputedStyle(measureElement).getPropertyValue('--choice-gap')) || 7
    currentItems.forEach((item, index) => {
      const itemHeight = heights[index] || 0
      const nextHeight = page.length ? pageHeight + gap + Math.min(itemHeight, height) : Math.min(itemHeight, height)
      if (page.length && nextHeight > height + 0.5) {
        nextPages.push(page)
        page = []
        pageHeight = 0
      }
      page.push(item)
      pageHeight += page.length > 1 ? gap + Math.min(itemHeight, height) : Math.min(itemHeight, height)
    })
    if (page.length || !nextPages.length) nextPages.push(page)
    setPages(previous => sameChoicePages(previous, nextPages) ? previous : nextPages)
    setPageIndex(previous => Math.min(previous, Math.max(nextPages.length - 1, 0)))
    setOversized(previous => {
      const same = previous.size === nextOversized.size && Array.from(previous).every(index => nextOversized.has(index))
      return same ? previous : nextOversized
    })
  }, [itemSelector, measureRef, viewportRef])

  useLayoutEffect(() => {
    setPages([items])
    setPageIndex(0)
    setOversized(new Set())
    retryCountRef.current = 0
    if (retryFrameRef.current !== null) window.cancelAnimationFrame(retryFrameRef.current)
    retryFrameRef.current = null
    const frame = window.requestAnimationFrame(measure)
    return () => {
      window.cancelAnimationFrame(frame)
      if (retryFrameRef.current !== null) window.cancelAnimationFrame(retryFrameRef.current)
      retryFrameRef.current = null
    }
  }, [items, measure, resetKey])

  useEffect(() => {
    const viewport = viewportRef.current
    if (!viewport) return
    let frame = 0
    let active = true
    const schedule = () => {
      if (!active) return
      window.cancelAnimationFrame(frame)
      frame = window.requestAnimationFrame(measure)
    }
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(schedule)
    observer?.observe(viewport)
    window.addEventListener('resize', schedule)
    const fonts = document.fonts
    const fontsReady = fonts?.ready.then(schedule)
    fonts?.addEventListener('loadingdone', schedule)
    return () => {
      window.cancelAnimationFrame(frame)
      if (retryFrameRef.current !== null) window.cancelAnimationFrame(retryFrameRef.current)
      retryFrameRef.current = null
      active = false
      observer?.disconnect()
      window.removeEventListener('resize', schedule)
      fonts?.removeEventListener('loadingdone', schedule)
      void fontsReady?.catch(() => undefined)
    }
  }, [measure, viewportRef])

  return { pages, pageIndex, setPageIndex, oversized, measure, isFirst: pageIndex === 0, isLast: pageIndex === pages.length - 1 }
}

type UseMeasuredPanelPagesOptions = {
  itemCount: number
  viewportRef: RefObject<HTMLElement | null>
  measureRef: RefObject<HTMLElement | null>
  resetKey: string
  itemSelector?: string
}

function sameIndexPages(a: number[][], b: number[][]): boolean {
  return a.length === b.length && a.every((page, pageIndex) => page.length === b[pageIndex].length && page.every((item, itemIndex) => item === b[pageIndex][itemIndex]))
}

/**
 * Splits arbitrary modal sections by their real rendered height. Only the
 * current page is mounted in the visible viewport; the hidden copy is used
 * solely to measure the same typography and width.
 */
export function useMeasuredPanelPages({ itemCount, viewportRef, measureRef, resetKey, itemSelector = '[data-panel-measure]' }: UseMeasuredPanelPagesOptions) {
  const firstPage = itemCount > 0 ? [[0]] : [[]]
  const [pages, setPages] = useState<number[][]>(firstPage)
  const [pageIndex, setPageIndex] = useState(0)
  const retryFrameRef = useRef<number | null>(null)
  const retryCountRef = useRef(0)

  const measure = useCallback(() => {
    const viewport = viewportRef.current
    const measureElement = measureRef.current
    if (!viewport || !measureElement || viewport.clientHeight <= 0) {
      if (retryFrameRef.current === null && retryCountRef.current < 12) {
        retryCountRef.current += 1
        retryFrameRef.current = window.requestAnimationFrame(() => {
          retryFrameRef.current = null
          measure()
        })
      }
      return
    }

    const nodes = Array.from(measureElement.querySelectorAll<HTMLElement>(itemSelector))
    if (nodes.length !== itemCount) return
    retryCountRef.current = 0
    const height = viewport.clientHeight
    measureElement.style.width = `${viewport.clientWidth}px`
    const gap = Number.parseFloat(getComputedStyle(measureElement).getPropertyValue('--panel-gap')) || 10
    const nextPages: number[][] = []
    let page: number[] = []
    let pageHeight = 0
    nodes.forEach((node, index) => {
      const itemHeight = Math.min(node.getBoundingClientRect().height, height)
      const nextHeight = page.length ? pageHeight + gap + itemHeight : itemHeight
      if (page.length && nextHeight > height + 0.5) {
        nextPages.push(page)
        page = []
        pageHeight = 0
      }
      page.push(index)
      pageHeight += page.length > 1 ? gap + itemHeight : itemHeight
    })
    if (page.length || !nextPages.length) nextPages.push(page)
    setPages(previous => sameIndexPages(previous, nextPages) ? previous : nextPages)
    setPageIndex(previous => Math.min(previous, Math.max(nextPages.length - 1, 0)))
  }, [itemCount, itemSelector, measureRef, viewportRef])

  useLayoutEffect(() => {
    setPages(itemCount > 0 ? [[0]] : [[]])
    setPageIndex(0)
    retryCountRef.current = 0
    if (retryFrameRef.current !== null) window.cancelAnimationFrame(retryFrameRef.current)
    retryFrameRef.current = null
    const frame = window.requestAnimationFrame(measure)
    return () => {
      window.cancelAnimationFrame(frame)
      if (retryFrameRef.current !== null) window.cancelAnimationFrame(retryFrameRef.current)
      retryFrameRef.current = null
    }
  }, [itemCount, measure, resetKey])

  useEffect(() => {
    const viewport = viewportRef.current
    if (!viewport) return
    let frame = 0
    let active = true
    const schedule = () => {
      if (!active) return
      window.cancelAnimationFrame(frame)
      frame = window.requestAnimationFrame(measure)
    }
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(schedule)
    observer?.observe(viewport)
    window.addEventListener('resize', schedule)
    const fonts = document.fonts
    const fontsReady = fonts?.ready.then(schedule)
    fonts?.addEventListener('loadingdone', schedule)
    return () => {
      window.cancelAnimationFrame(frame)
      if (retryFrameRef.current !== null) window.cancelAnimationFrame(retryFrameRef.current)
      retryFrameRef.current = null
      active = false
      observer?.disconnect()
      window.removeEventListener('resize', schedule)
      fonts?.removeEventListener('loadingdone', schedule)
      void fontsReady?.catch(() => undefined)
    }
  }, [measure, viewportRef])

  return { pages, pageIndex, setPageIndex, measure, isFirst: pageIndex === 0, isLast: pageIndex === pages.length - 1 }
}
